/**
 * Plays gym-coach voice clips. Both English and Polish now ship pre-recorded
 * native-accent MP3s in a male AND a female voice (generated via edge-tts —
 * see backend/scripts/gen_polish_coach.py), so the coach sounds energetic and
 * the Male/Female toggle works in every language. Files:
 *   /sfx/coach_<lang>_<gender>_<name>.mp3   (lang = en|pl, gender = m|f)
 *
 * Respects the global mute toggle (lib/sound.js).
 */
import { isCoachVoiceMuted } from "./sound";
import i18n from "../i18n";

const _cache = {};

// Coach voice gender preference (persisted). Default male.
export function getCoachGender() {
  try { return localStorage.getItem("fitjong_coach_gender") === "f" ? "f" : "m"; }
  catch (_) { return "m"; }
}
export function setCoachGender(g) {
  try { localStorage.setItem("fitjong_coach_gender", g === "f" ? "f" : "m"); } catch (_) {}
  // Drop ALL cached <audio> so the next line/countdown loads the chosen
  // voice immediately (fixes "switching female→male doesn't take effect").
  for (const k of Object.keys(_cache)) delete _cache[k];
}

// Resolve the concrete clip filename for the active language + chosen gender.
function _clipName(name) {
  const lang = (i18n.language || "en").toLowerCase().startsWith("pl") ? "pl" : "en";
  return `${lang}_${getCoachGender()}_${name}`;
}

function _audio(name) {
  if (!_cache[name]) {
    const a = new Audio(`/sfx/coach_${name}.mp3`);
    a.preload = "auto";
    // The "3,2,1,start" line punches through ambient noise at full volume;
    // all other coach lines stay a touch gentler.
    a.volume = name.endsWith("321_go") ? 1.0 : 0.9;
    _cache[name] = a;
  }
  return _cache[name];
}

function _play(name) {
  try {
    const a = _audio(_clipName(name));
    a.currentTime = 0;
    const p = a.play();
    if (p && p.catch) p.catch(() => {}); // autoplay policies may reject — silent
  } catch (_) { /* audio missing or autoplay rejected */ }
}

export function playCoach(name) {
  if (isCoachVoiceMuted()) return;
  _play(name);
}

const WIN_LINES = ["well_done", "good_job", "beast", "crushed_it"];
export function playCoachWin(avatarLine) {
  if (isCoachVoiceMuted()) return;
  let pick;
  if (avatarLine && Math.random() < 0.6) {
    pick = avatarLine;
  } else {
    pick = WIN_LINES[Math.floor(Math.random() * WIN_LINES.length)];
  }
  _play(pick);
}

export function playCoachCombo(streak) {
  if (isCoachVoiceMuted()) return;
  if (streak === 3) _play("combo");
  else if (streak === 5) _play("on_fire");
  else if (streak >= 7) _play("unstoppable");
}

export function playCoachTimeUp() {
  playCoach("time_up");
}

export function playCoachStart() {
  playCoach("321_go");
}

// Countdown numbers — spoken (gendered, native accent) in sync with the
// on-screen 3/2/1/GO, in whichever language the app is set to.
const CD_CLIP = { 3: "cd_three", 2: "cd_two", 1: "cd_one", GO: "cd_go" };
export function playCoachCountdown(step) {
  if (isCoachVoiceMuted()) return;
  const name = CD_CLIP[step];
  if (!name) return;
  _play(name);
}
